/* ============================================
   PRODUCT CUSTOM - JavaScript
   ============================================ */

class ProductCustom {
  constructor() {
    this.container = document.querySelector('.product-custom-container');
    if (!this.container) return;

    this.productId = this.container.dataset.productId;
    this.currentVariant = null;
    this.productData = null;

    this.init();
  }

  init() {
    this.loadProductData();
    this.loadMediaData();
    this.setupQuantityButtons();
    this.setupVariantSelector();
    this.setupCustomizerCheckbox();
    this.setupPersonalizationField();
    this.setupAddToCart();
    this.setupBuyNow();
    this.setupUpsell();
    this.setupDescription();
    this.setupSizeGuide();
    this.setupShare();
    this.setupMobileGallery();
    this.setupDesktopGalleryZoom();
    this.setupBundle();
  }

  /* ========== Customizer Checkbox (terms acceptance) ========== */
  setupCustomizerCheckbox() {
    const checkbox = document.querySelector('input[name="properties[is_customized]"]');
    const timestampInput = document.querySelector('input[name="properties[_Aceite dos termos]"]');

    if (!checkbox || !timestampInput) return;

    checkbox.addEventListener('change', () => {
      if (checkbox.checked) {
        timestampInput.value = new Date().toLocaleString('pt-BR');
      } else {
        timestampInput.value = '';
      }
    });

    /* Como o app externo de customização intercepta o submit e dispara seu
       próprio /cart/add.js sem incluir o input deste form, fazemos um patch
       pós-adição via /cart/change.js para garantir que o `_Aceite dos termos`
       seja gravado no line item. */
    const button = document.querySelector('[data-add-to-cart]');
    if (button) {
      button.addEventListener('click', () => {
        if (!checkbox.checked || !timestampInput.value) return;
        const variantInput = document.querySelector('[data-variant-id]');
        const variantId = parseInt(this.currentVariant?.id || variantInput?.value);
        if (!variantId) return;
        this.patchTermsAcceptance(variantId, timestampInput.value);
      });
    }
  }

  async patchTermsAcceptance(variantId, timestamp) {
    const TERMS_PROP = '_Aceite dos termos';
    const maxAttempts = 25;
    const delayMs = 250;

    let initialKeys = new Set();
    try {
      const initialCart = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
      initialKeys = new Set(initialCart.items.map(i => i.key));
    } catch (e) {
      // segue mesmo sem snapshot inicial
    }

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(r => setTimeout(r, delayMs));

      let cart;
      try {
        cart = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
      } catch (e) {
        continue;
      }

      const itemIndex = cart.items.findIndex(item =>
        item.id === variantId
        && (!item.properties || item.properties[TERMS_PROP] === undefined)
        && (!initialKeys.size || !initialKeys.has(item.key))
      );

      if (itemIndex === -1) continue;

      const item = cart.items[itemIndex];
      const newProperties = { ...(item.properties || {}), [TERMS_PROP]: timestamp };

      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({
            line: itemIndex + 1,
            quantity: item.quantity,
            properties: newProperties,
          }),
        });
      } catch (e) {
        console.warn('[piuka] Falha ao gravar _Aceite dos termos:', e);
      }

      return;
    }
  }

  /* ========== Campo de personalização (app externo) ========== */
  setupPersonalizationField() {
    let maxChars = null;
    document.querySelectorAll('.variant-buttons[data-option-name]').forEach((wrapper) => {
      const name = (wrapper.dataset.optionName || '').toLowerCase();
      if (!name.includes('caractere')) return;
      const source = wrapper.querySelector('.variant-button.active') || wrapper.querySelector('.variant-button');
      const match = source && source.textContent.match(/\d+/);
      if (match) maxChars = parseInt(match[0], 10);
    });

    const apply = () => {
      const input = document.querySelector(
        '.product-customizer-options input[type="text"], .product-customizer-options textarea'
      );
      if (!input) return false;

      let limit = maxChars;
      const inputMax = parseInt(input.getAttribute('maxlength'), 10);
      if (!limit && inputMax) limit = inputMax;

      if (limit && !input.getAttribute('maxlength')) {
        input.setAttribute('maxlength', String(limit));
      }
      if (!input.getAttribute('placeholder') || input.getAttribute('placeholder').trim() === '') {
        input.setAttribute('placeholder', 'Insira o nome');
      }

      if (limit) {
        const option = input.closest('.product-customizer-option');
        const label = option ? option.querySelector('label') : null;
        if (label && !label.querySelector('.personalization-max-hint')) {
          const padded = limit < 10 ? '0' + limit : String(limit);
          const hint = document.createElement('span');
          hint.className = 'personalization-max-hint';
          hint.textContent = '(Máximo ' + padded + ' caracteres)';
          label.appendChild(hint);
        }
      }

      return true;
    };

    if (apply()) return;

    const observer = new MutationObserver(() => {
      if (apply()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 15000);
  }

  /* ========== Load Product Data (sync from inline JSON) ========== */
  loadProductData() {
    // 1) Try inline variants JSON (synchronous, no race condition)
    try {
      const variantsScript = document.querySelector('[data-product-variants]');
      if (variantsScript) {
        const variants = JSON.parse(variantsScript.textContent);
        this.productData = { variants: variants };
      }
    } catch (e) {
      console.warn('Inline variants JSON parse failed:', e);
    }

    // 2) Fallback: fetch from .js endpoint (async)
    if (!this.productData) {
      this.fetchProductDataAsync();
      return;
    }

    this.onProductDataReady();
  }

  async fetchProductDataAsync() {
    try {
      const response = await fetch(`${window.location.pathname}.js`);
      this.productData = await response.json();
      this.onProductDataReady();
    } catch (error) {
      console.error('Erro ao carregar dados do produto:', error);
    }
  }

  onProductDataReady() {
    // Set initial variant from hidden input or first available
    const variantIdInput = document.querySelector('[data-variant-id]');
    const initialId = variantIdInput ? parseInt(variantIdInput.value) : null;
    this.currentVariant = this.productData.variants.find(v => v.id === initialId)
      || this.productData.variants.find(v => v.available)
      || this.productData.variants[0];

    this.updateSoldOutVariants();
  }

  /* ========== Load Media Data ========== */
  loadMediaData() {
    this.allMedia = [];
    this.variantImagesMap = {};

    const mediaGrid = document.querySelector('.product-media-grid');
    const maxImages = mediaGrid?.dataset.maxImages ? parseInt(mediaGrid.dataset.maxImages, 10) : null;

    try {
      const mediaScript = document.querySelector('[data-product-media]');
      if (mediaScript) {
        let media = JSON.parse(mediaScript.textContent);
        if (maxImages && maxImages > 0) {
          media = media.slice(0, maxImages);
        }
        this.allMedia = media;
      }
    } catch (e) {
      console.warn('Media data parse failed:', e);
    }

    try {
      const variantImagesScript = document.querySelector('[data-variant-images]');
      if (variantImagesScript) {
        this.variantImagesMap = JSON.parse(variantImagesScript.textContent);
      }
    } catch (e) {
      console.warn('Variant images map parse failed:', e);
    }
  }

  /* ========== Quantity Selector ========== */
  setupQuantityButtons() {
    const minusBtn = document.querySelector('[data-quantity-minus]');
    const plusBtn = document.querySelector('[data-quantity-plus]');
    const input = document.querySelector('[data-quantity-input]');

    if (!input) return;

    minusBtn?.addEventListener('click', () => {
      const currentValue = parseInt(input.value) || 1;
      if (currentValue > 1) {
        input.value = currentValue - 1;
      }
    });

    plusBtn?.addEventListener('click', () => {
      const currentValue = parseInt(input.value) || 1;
      input.value = currentValue + 1;
    });

    input.addEventListener('change', () => {
      if (parseInt(input.value) < 1) {
        input.value = 1;
      }
    });
  }

  /* ========== Customization validation & properties ========== */
  isCustomizationRequired() {
    const wrapper = document.getElementById('customizer-checkbox');
    if (!wrapper) return false;
    return wrapper.style.display !== 'none';
  }

  validateCustomizationRequirements() {
    const wrapper = document.getElementById('customizer-checkbox');
    if (!wrapper) return true;
    if (!this.isCustomizationRequired()) return true;

    const checked = wrapper.querySelector('input[type="checkbox"]:checked');
    if (!checked) {
      wrapper.classList.add('error');
      return false;
    }

    const customizerInput = document.querySelector('.product-customizer-options input[type="text"]');
    if (customizerInput && customizerInput.value.trim().length === 0) {
      wrapper.classList.add('error');
      return false;
    }

    wrapper.classList.remove('error');
    return true;
  }

  getLineItemProperties(form) {
    const properties = {};

    try {
      const formData = new FormData(form);
      for (const [key, value] of formData.entries()) {
        const match = /^properties\[(.+)\]$/.exec(key);
        if (!match) continue;
        const propName = match[1];
        properties[propName] = value;
      }
    } catch (e) {
      // ignore
    }

    /* Apps de customização (ex.: Product Customizer) podem manter inputs
       `properties[...]` fora do form ou só sincronizam o value no submit.
       Para garantir que esses valores cheguem ao /cart/add.js, varremos o
       documento e preenchemos apenas as properties que ainda estão vazias. */
    try {
      const propInputs = document.querySelectorAll(
        'input[name^="properties["], textarea[name^="properties["], select[name^="properties["]'
      );
      propInputs.forEach((input) => {
        const match = /^properties\[(.+)\]$/.exec(input.name || '');
        if (!match) return;
        const propName = match[1];
        const value = input.type === 'checkbox' || input.type === 'radio'
          ? (input.checked ? (input.value || 'on') : '')
          : input.value;
        if (value && !properties[propName]) {
          properties[propName] = value;
        }
      });
    } catch (e) {
      // ignore
    }

    /* Garante is_customized='on' sempre que o checkbox estiver marcado,
       independente do FormData ter capturado (o checkbox fica fora do form
       e é associado via atributo `form`). */
    const isCustomizedCheckbox = form.querySelector('input[name="properties[is_customized]"]')
      || document.querySelector('input[name="properties[is_customized]"]');
    if (isCustomizedCheckbox?.checked) {
      properties.is_customized = 'on';
    } else if (isCustomizedCheckbox) {
      delete properties.is_customized;
      delete properties['_Aceite dos termos'];
    }

    if (!properties['_Aceite dos termos']) {
      delete properties['_Aceite dos termos'];
    }

    return properties;
  }

  /* ========== Variant Selector ========== */
  setupVariantSelector() {
    const variantButtons = document.querySelectorAll('.variant-button');
    
    variantButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const btn = e.currentTarget;
        const optionValue = btn.dataset.optionValue;
        const optionPosition = parseInt(btn.dataset.optionPosition);
        
        // Remove active class from siblings
        const siblings = btn.parentElement.querySelectorAll('.variant-button');
        siblings.forEach(b => b.classList.remove('active'));
        
        // Add active to clicked
        btn.classList.add('active');
        
        // Update variant
        this.updateSelectedVariant();
      });
    });
  }

  getSelectedOptions() {
    const selectedOptions = [];
    document.querySelectorAll('.variant-buttons').forEach(wrapper => {
      const activeBtn = wrapper.querySelector('.variant-button.active');
      if (activeBtn) {
        selectedOptions.push(String(activeBtn.dataset.optionValue).trim());
      }
    });
    return selectedOptions;
  }

  findVariantByOptions(selectedOptions) {
    if (!this.productData || !this.productData.variants) return null;
    return this.productData.variants.find(v => {
      for (let i = 0; i < selectedOptions.length; i++) {
        const vOpt = String(v['option' + (i + 1)] || '').trim();
        if (vOpt !== selectedOptions[i]) return false;
      }
      return true;
    });
  }

  updateSelectedVariant() {
    if (!this.productData) return;

    const selectedOptions = this.getSelectedOptions();
    if (selectedOptions.length === 0) return;

    const variant = this.findVariantByOptions(selectedOptions);

    if (variant) {
      this.currentVariant = variant;
      this.updateVariantUI(variant);
    }
  }

  updateVariantUI(variant) {
    // Update price
    const priceElement = document.querySelector('[data-price]');
    const priceWrapper = document.querySelector('.product-price-block .price-wrapper');
    const visualPromotionActive = priceWrapper?.dataset.visualPromotion === 'true';
    const visualPromotionPercentage = Number(priceWrapper?.dataset.visualPromotionPercentage || 0);
    const displayedPrice = visualPromotionActive
      ? Math.floor(variant.price * (100 - visualPromotionPercentage) / 100)
      : variant.price;

    if (priceElement && variant.price) {
      priceElement.textContent = this.formatMoney(displayedPrice);
    }

    if (visualPromotionActive) {
      const comparePriceElement = priceWrapper.querySelector('[data-price-compare]');
      const badgeElement = priceWrapper.querySelector('[data-price-badge]');

      if (comparePriceElement) {
        comparePriceElement.textContent = this.formatMoney(variant.price);
      }

      if (badgeElement) {
        badgeElement.textContent = `${visualPromotionPercentage}% OFF`;
      }
    }

    // Update SKU
    const skuElement = document.querySelector('[data-sku]');
    if (skuElement && variant.sku) {
      skuElement.textContent = variant.sku;
    }

    // Update variant ID in form
    const variantIdInput = document.querySelector('[data-variant-id]');
    if (variantIdInput) {
      variantIdInput.value = variant.id;
    }

    // Update button states and text
    const addToCartBtn = document.querySelector('[data-add-to-cart]');
    const buyNowBtn = document.querySelector('[data-buy-now]');
    
    if (variant.available) {
      addToCartBtn?.removeAttribute('disabled');
      buyNowBtn?.removeAttribute('disabled');
      if (addToCartBtn) {
        addToCartBtn.textContent = addToCartBtn.dataset.addText || 'Adicionar ao carrinho';
      }
    } else {
      addToCartBtn?.setAttribute('disabled', 'disabled');
      buyNowBtn?.setAttribute('disabled', 'disabled');
      if (addToCartBtn) {
        addToCartBtn.textContent = addToCartBtn.dataset.soldOutText || 'Esgotado';
      }
    }

    // Update low stock warning
    this.updateLowStock(variant);

    // Update sold-out state on variant buttons
    this.updateSoldOutVariants();

    // Update product images for this variant
    this.updateProductImages(variant);
  }

  updateLowStock(variant) {
    const lowStockEl = document.querySelector('[data-low-stock]');
    if (!lowStockEl) return;

    if (!this.variantInventory) {
      const inventoryScript = document.querySelector('[data-variant-inventory]');
      try {
        this.variantInventory = inventoryScript ? JSON.parse(inventoryScript.textContent) : {};
      } catch (e) {
        this.variantInventory = {};
      }
    }

    const inventory = this.variantInventory[variant.id];
    const threshold = parseInt(lowStockEl.dataset.threshold, 10) || 5;
    const qty = inventory ? inventory.qty : 0;
    const visible = Boolean(inventory && inventory.tracked && qty > 0 && qty <= threshold);

    lowStockEl.hidden = !visible;
    if (!visible) return;

    const template = qty === 1 && lowStockEl.dataset.textSingular
      ? lowStockEl.dataset.textSingular
      : lowStockEl.dataset.text;
    const textEl = lowStockEl.querySelector('[data-low-stock-text]');
    if (textEl) {
      textEl.textContent = template.split('[quantidade]').join(qty);
    }
  }

  updateSoldOutVariants() {
    if (!this.productData) return;

    const variantWrappers = document.querySelectorAll('.variant-buttons');
    
    variantWrappers.forEach((wrapper, wrapperIndex) => {
      const buttons = wrapper.querySelectorAll('.variant-button');
      
      buttons.forEach(button => {
        const optionValue = String(button.dataset.optionValue).trim();
        const optionPosition = parseInt(button.dataset.optionPosition) - 1;
        
        // Get currently selected options
        const selectedOptions = this.getSelectedOptions();

        // Check if this option value leads to any available variant
        let hasAvailableVariant = false;
        
        this.productData.variants.forEach(variant => {
          // Create a test options array with this button's value
          const testOptions = [...selectedOptions];
          testOptions[optionPosition] = optionValue;
          
          // Check if variant matches these options using option1/option2/option3
          let matches = true;
          for (let i = 0; i < testOptions.length; i++) {
            if (testOptions[i] === undefined) continue;
            const vOpt = String(variant['option' + (i + 1)] || '').trim();
            if (vOpt !== testOptions[i]) {
              matches = false;
              break;
            }
          }
          
          if (matches && variant.available) {
            hasAvailableVariant = true;
          }
        });

        // Update button sold-out state
        if (hasAvailableVariant) {
          button.classList.remove('sold-out');
          button.removeAttribute('data-sold-out');
        } else {
          button.classList.add('sold-out');
          button.setAttribute('data-sold-out', 'true');
        }
      });
    });
  }

  /* ========== Update Product Images ========== */
  updateProductImages(variant) {
    if (!this.allMedia || !this.allMedia.length || !this.variantImagesMap) return;

    const featuredData = this.variantImagesMap[String(variant.id)];
    if (!featuredData || !featuredData.id) return;

    const media = this.allMedia.find(m => m.id === featuredData.id);
    if (!media) return;

    const mainDesktop = document.querySelector('.media-grid-desktop .media-main img');
    if (mainDesktop) {
      mainDesktop.src = media.src;
      mainDesktop.srcset = media.srcset;
      mainDesktop.alt = media.alt || '';
      mainDesktop.width = media.width;
      mainDesktop.height = media.height;
    }

    const slideIndex = this.allMedia.findIndex(m => m.id === featuredData.id);
    const mobileCount = this.mobileSlidesCount || 0;
    if (slideIndex >= 0 && slideIndex < mobileCount && typeof this.mobileGoToSlide === 'function') {
      this.mobileGoToSlide(slideIndex);
    } else if (slideIndex >= 0) {
      const thumbnails = document.querySelectorAll('.thumbnail-item');
      thumbnails.forEach((t, i) => t.classList.toggle('active', i === slideIndex));
      const activeThumb = thumbnails[slideIndex];
      if (activeThumb) {
        activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    }
  }

  /* ========== Add to Cart ========== */
  setupAddToCart() {
    const form = document.querySelector(`#product-form-${this.getSectionId()}`);
    const button = document.querySelector('[data-add-to-cart]');

    if (!form || !button) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      if (!this.validateCustomizationRequirements()) return;

      const variantId = this.currentVariant ? this.currentVariant.id : document.querySelector('[data-variant-id]')?.value;
      const quantity = parseInt(document.querySelector('[data-quantity-input]')?.value) || 1;

      if (!variantId) return;
      const properties = this.getLineItemProperties(form);
      this.addToCartRequest(variantId, quantity, button, properties);
    });
  }

  async addToCartRequest(variantId, quantity, button, properties = undefined) {
    const originalText = button.textContent;
    button.classList.add('loading');
    
    try {
      const cartDrawer = document.querySelector('cart-drawer');
      const sections = cartDrawer ? cartDrawer.getSectionsToRender().map((section) => section.id) : [];

      const body = {
        id: variantId,
        quantity: quantity,
        sections: sections.join(','),
        sections_url: window.location.pathname,
      };

      if (properties && Object.keys(properties).length > 0) {
        body.properties = properties;
      }
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const data = await response.json();
        this.showMessage('Produto adicionado ao carrinho!', 'success');
        this.updateCartCount();
        
        // Open cart drawer with proper Shopify integration
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(data);
        }
        
        // Publish cart update event for other components
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-custom',
            cartData: data
          });
        }
      } else {
        throw new Error('Erro ao adicionar ao carrinho');
      }
    } catch (error) {
      console.error('Erro:', error);
      this.showMessage('Erro ao adicionar ao carrinho. Tente novamente.', 'error');
    } finally {
      button.classList.remove('loading');
    }
  }

  /* ========== Buy Now ========== */
  setupBuyNow() {
    const button = document.querySelector('[data-buy-now]');
    
    if (!button) return;

    button.addEventListener('click', async () => {
      const form = document.querySelector(`#product-form-${this.getSectionId()}`);
      if (!form) return;

      if (!this.validateCustomizationRequirements()) return;

      const variantId = this.currentVariant ? this.currentVariant.id : document.querySelector('[data-variant-id]')?.value;
      const quantity = parseInt(document.querySelector('[data-quantity-input]')?.value) || 1;

      if (!variantId) return;

      const properties = this.getLineItemProperties(form);

      button.classList.add('loading');

      try {
        // Add to cart first
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: quantity,
            ...(properties && Object.keys(properties).length > 0 ? { properties } : {}),
          })
        });

        if (response.ok) {
          // Redirect to checkout
          window.location.href = '/checkout';
        } else {
          throw new Error('Erro ao processar compra');
        }
      } catch (error) {
        console.error('Erro:', error);
        this.showMessage('Erro ao processar compra. Tente novamente.', 'error');
        button.classList.remove('loading');
      }
    });
  }

  /* ========== Upsell ========== */
  setupUpsell() {
    const upsellCards = document.querySelectorAll('.upsell-card');
    
    upsellCards.forEach(card => {
      const variantSelectors = card.querySelectorAll('[data-upsell-variant-selector]');
      const addButton = card.querySelector('[data-upsell-add]');
      const variantsDataEl = card.querySelector('[data-upsell-variants]');
      const priceEl = card.querySelector('[data-upsell-price]');
      
      if (variantSelectors.length > 0 && variantsDataEl) {
        const variants = JSON.parse(variantsDataEl.textContent);
        
        // Update variant on select change
        variantSelectors.forEach(select => {
          select.addEventListener('change', () => {
            const selectedOptions = [];
            variantSelectors.forEach(sel => {
              selectedOptions.push(sel.value);
            });
            
            // Find matching variant
            const matchedVariant = variants.find(variant => {
              return selectedOptions.every((option, index) => {
                return variant.options[index] === option;
              });
            });
            
            if (matchedVariant) {
              // Update button with new variant ID
              addButton.dataset.productId = matchedVariant.id;
              
              // Update price
              if (priceEl) {
                priceEl.textContent = 'Por apenas ' + this.formatMoney(matchedVariant.price);
              }
              
              // Update button availability
              if (matchedVariant.available) {
                addButton.removeAttribute('disabled');
              } else {
                addButton.setAttribute('disabled', 'disabled');
              }
            }
          });
        });
      }
      
      // Setup add to cart button
      if (addButton) {
        addButton.addEventListener('click', async (e) => {
          e.preventDefault();
          const productId = e.currentTarget.dataset.productId;
          
          if (!productId) {
            console.error('Product ID not found on upsell button');
            return;
          }

          this.addToCartRequest(productId, 1, e.currentTarget);
        });
      }
    });
  }

  /* ========== Description Accordion ========== */
  setupDescription() {
    const toggleButton = document.querySelector('[data-description-toggle]');
    const fullDescription = document.querySelector('[data-description-full]');
    const previewDescription = document.querySelector('[data-description-preview]');
    
    if (!toggleButton || !fullDescription || !previewDescription) return;

    toggleButton.addEventListener('click', () => {
      const isExpanded = fullDescription.classList.contains('active');
      
      if (isExpanded) {
        fullDescription.classList.remove('active');
        fullDescription.style.display = 'none';
        previewDescription.style.display = 'block';
        toggleButton.textContent = 'Ler descrição completa';
      } else {
        fullDescription.classList.add('active');
        fullDescription.style.display = 'block';
        previewDescription.style.display = 'none';
        toggleButton.textContent = 'Ocultar descrição';
      }
    });
  }

  /* ========== Size Guide Modal ========== */
  setupSizeGuide() {
    const trigger = document.querySelector('[data-size-guide-trigger]');
    const modal = document.querySelector('[data-size-guide-modal]');
    const closeButtons = document.querySelectorAll('[data-modal-close]');
    
    if (!trigger || !modal) return;

    trigger.addEventListener('click', () => {
      modal.style.display = 'flex';
      document.body.style.overflow = 'hidden';
    });

    closeButtons.forEach(button => {
      button.addEventListener('click', () => {
        modal.style.display = 'none';
        document.body.style.overflow = '';
      });
    });

    // Close on overlay click
    modal.querySelector('.modal-overlay')?.addEventListener('click', () => {
      modal.style.display = 'none';
      document.body.style.overflow = '';
    });
  }

  /* ========== Share ========== */
  setupShare() {
    const whatsappBtn = document.querySelector('[data-share-whatsapp]');
    const copyBtn = document.querySelector('[data-share-copy]');
    
    if (whatsappBtn) {
      whatsappBtn.addEventListener('click', () => {
        const url = window.location.href;
        const text = document.querySelector('.product-title')?.textContent || '';
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text + ' - ' + url)}`;
        window.open(whatsappUrl, '_blank');
      });
    }

    if (copyBtn) {
      copyBtn.addEventListener('click', async () => {
        const url = window.location.href;
        
        try {
          await navigator.clipboard.writeText(url);
          this.showMessage('Link copiado!', 'success');
        } catch (error) {
          // Fallback for older browsers
          const textarea = document.createElement('textarea');
          textarea.value = url;
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
          this.showMessage('Link copiado!', 'success');
        }
      });
    }
  }

  /* ========== Mobile Gallery ========== */
  setupMobileGallery() {
    const sliderTrack = document.querySelector('.mobile-slider-track');
    const thumbnails = document.querySelectorAll('.thumbnail-item');

    if (!sliderTrack) return;

    const realSlides = Array.from(sliderTrack.querySelectorAll('.mobile-slide:not(.is-clone)'));
    const totalSlides = realSlides.length;
    if (totalSlides === 0) return;

    this.mobileSlidesCount = totalSlides;

    // Loop infinito só faz sentido com mais de uma imagem
    const hasLoop = totalSlides > 1;

    // Clona a última imagem antes da primeira e a primeira depois da última
    if (hasLoop && !sliderTrack.dataset.loopReady) {
      const firstClone = realSlides[0].cloneNode(true);
      const lastClone = realSlides[totalSlides - 1].cloneNode(true);
      firstClone.classList.add('is-clone');
      lastClone.classList.add('is-clone');
      sliderTrack.appendChild(firstClone);
      sliderTrack.insertBefore(lastClone, realSlides[0]);
      sliderTrack.dataset.loopReady = 'true';
    }

    // offsetBase: posição (na faixa com clones) da primeira imagem real
    const offsetBase = hasLoop ? 1 : 0;
    let currentIndex = 0; // índice da imagem REAL exibida
    let position = offsetBase; // posição na faixa (inclui clones)

    const setTransform = (pos, animate) => {
      sliderTrack.style.transition = animate ? '' : 'none';
      sliderTrack.style.transform = `translateX(-${pos * 100}%)`;
    };

    const updateThumbs = () => {
      thumbnails.forEach((t, i) => t.classList.toggle('active', i === currentIndex));
      const activeThumb = thumbnails[currentIndex];
      if (activeThumb) {
        activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    };

    // Vai direto para uma imagem real (usado por thumbnails e troca de variante)
    const goToSlide = (index) => {
      currentIndex = (index % totalSlides + totalSlides) % totalSlides;
      position = currentIndex + offsetBase;
      setTransform(position, true);
      updateThumbs();
    };
    this.mobileGoToSlide = goToSlide;

    // Avança/retrocede uma posição, atravessando os clones quando necessário
    const step = (dir) => {
      if (!hasLoop) return;
      position += dir;
      currentIndex = ((position - offsetBase) % totalSlides + totalSlides) % totalSlides;
      setTransform(position, true);
      updateThumbs();
    };

    // Ao terminar a transição sobre um clone, salta (sem animação) para a imagem real
    sliderTrack.addEventListener('transitionend', (e) => {
      if (!hasLoop || e.propertyName !== 'transform') return;
      if (position === 0) {
        position = totalSlides;
        setTransform(position, false);
      } else if (position === totalSlides + 1) {
        position = 1;
        setTransform(position, false);
      }
    });

    // Posição inicial (após os clones)
    setTransform(position, false);

    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => goToSlide(index));
    });

    this.goToMobileSlide = goToSlide;
    this.getMobileIndex = () => currentIndex;

    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartTime = 0;
    let isDragging = false;

    sliderTrack.addEventListener('touchstart', (e) => {
      this._isTouchDevice = true;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      touchStartTime = Date.now();
      isDragging = false;
    }, { passive: true });

    sliderTrack.addEventListener('touchmove', (e) => {
      const dx = e.touches[0].clientX - touchStartX;
      const dy = e.touches[0].clientY - touchStartY;
      if (!isDragging && Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 8) {
        isDragging = true;
      }
      if (isDragging) {
        e.preventDefault();
        const trackWidth = sliderTrack.parentElement.offsetWidth;
        const offset = (-position * 100) + (dx / trackWidth) * 100;
        sliderTrack.style.transition = 'none';
        sliderTrack.style.transform = `translateX(${offset}%)`;
      }
    }, { passive: false });

    sliderTrack.addEventListener('touchend', (e) => {
      if (!isDragging) {
        const dx = Math.abs(e.changedTouches[0].clientX - touchStartX);
        const dy = Math.abs(e.changedTouches[0].clientY - touchStartY);
        const elapsed = Date.now() - touchStartTime;
        if (dx < 10 && dy < 10 && elapsed < 400) {
          this.openMobileLightbox(currentIndex);
        }
        return;
      }
      sliderTrack.style.transition = '';
      const dx = e.changedTouches[0].clientX - touchStartX;
      const threshold = sliderTrack.parentElement.offsetWidth * 0.25;
      if (dx < -threshold) {
        hasLoop ? step(1) : goToSlide(currentIndex + 1);
      } else if (dx > threshold) {
        hasLoop ? step(-1) : goToSlide(currentIndex - 1);
      } else {
        setTransform(position, true);
      }
      isDragging = false;
    }, { passive: true });

    /* Fallback apenas para dispositivos com mouse (em toque, o touchend já trata) */
    sliderTrack.querySelectorAll('.mobile-slide').forEach((slide, index) => {
      slide.addEventListener('click', (e) => {
        if (this._isTouchDevice) return;
        e.preventDefault();
        this.openMobileLightbox(index);
      });
    });
  }

  /* ========== Desktop Gallery Zoom ========== */
  setupDesktopGalleryZoom() {
    const items = document.querySelectorAll('.media-grid-desktop .media-item');
    if (!items.length || !this.allMedia || !this.allMedia.length) return;

    items.forEach((item) => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        const mediaId = item.dataset.mediaId;
        let index = this.allMedia.findIndex(m => String(m.id) === String(mediaId));
        if (index < 0) {
          const img = item.querySelector('img');
          index = img ? this.allMedia.findIndex(m => m.src.split('?')[0] === img.src.split('?')[0]) : 0;
        }
        this.openMobileLightbox(index < 0 ? 0 : index);
      });
    });
  }

  /* ========== Image Lightbox (tela cheia com pan no eixo Y) ========== */
  buildMobileLightbox() {
    if (this.lightbox || !this.allMedia || !this.allMedia.length) return;

    const overlay = document.createElement('div');
    overlay.className = 'mobile-image-lightbox';
    overlay.setAttribute('aria-hidden', 'true');

    const thumbsHtml = this.allMedia.map((media, i) => {
      const thumb = media.thumbnail || media.src;
      const alt = (media.alt || '').replace(/"/g, '&quot;');
      return `
        <button type="button" class="lightbox-thumb" data-lightbox-thumb="${i}" aria-label="Ver imagem ${i + 1}">
          <img src="${thumb}" alt="${alt}" loading="lazy" draggable="false">
        </button>`;
    }).join('');

    overlay.innerHTML = `
      <div class="lightbox-topbar">
        <span class="lightbox-counter" data-lightbox-counter>1 / ${this.allMedia.length}</span>
        <button type="button" class="lightbox-btn" data-lightbox-close aria-label="Fechar">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 6L6 18M6 6l12 12"></path>
          </svg>
        </button>
      </div>
      <button type="button" class="lightbox-nav lightbox-prev" data-lightbox-prev aria-label="Imagem anterior">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M15 18l-6-6 6-6"></path>
        </svg>
      </button>
      <button type="button" class="lightbox-nav lightbox-next" data-lightbox-next aria-label="Próxima imagem">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M9 18l6-6-6-6"></path>
        </svg>
      </button>
      <div class="lightbox-stage" data-lightbox-stage>
        <img class="lightbox-image" data-lightbox-image alt="" draggable="false">
      </div>
      <div class="lightbox-thumbs" data-lightbox-thumbs>
        ${thumbsHtml}
      </div>
    `;
    document.body.appendChild(overlay);

    this.lightbox = {
      overlay,
      counter: overlay.querySelector('[data-lightbox-counter]'),
      stage: overlay.querySelector('[data-lightbox-stage]'),
      image: overlay.querySelector('[data-lightbox-image]'),
      prev: overlay.querySelector('[data-lightbox-prev]'),
      next: overlay.querySelector('[data-lightbox-next]'),
      thumbsRail: overlay.querySelector('[data-lightbox-thumbs]'),
      thumbEls: Array.from(overlay.querySelectorAll('[data-lightbox-thumb]')),
      index: 0,
      panRatio: 0.5,
      panRatioX: 0.5,
      overflow: 0,
      overflowX: 0,
    };

    overlay.querySelector('[data-lightbox-close]').addEventListener('click', () => this.closeMobileLightbox());
    this.lightbox.prev.addEventListener('click', () => this.lightboxGo(this.lightbox.index - 1));
    this.lightbox.next.addEventListener('click', () => this.lightboxGo(this.lightbox.index + 1));

    this.lightbox.thumbEls.forEach((el) => {
      el.addEventListener('click', () => this.lightboxGo(parseInt(el.dataset.lightboxThumb, 10)));
    });

    /* Pan no eixo Y seguindo o mouse (desktop) */
    this.lightbox.stage.addEventListener('mouseenter', () => {
      if (!this._isTouchDevice) this.lightbox.image.classList.add('is-panning');
    });
    this.lightbox.stage.addEventListener('mouseleave', () => {
      if (!this._isTouchDevice) this.lightbox.image.classList.remove('is-panning');
    });
    this.lightbox.stage.addEventListener('mousemove', (e) => {
      if (this._isTouchDevice || this.lightbox.overflow <= 0) return;
      const rect = this.lightbox.stage.getBoundingClientRect();
      this.applyLightboxPan(0.5, (e.clientY - rect.top) / rect.height);
    });

    /* Navegação por teclado no desktop */
    this._lightboxKeyHandler = (e) => {
      if (!this.lightbox || !this.lightbox.overlay.classList.contains('is-open')) return;
      if (e.key === 'Escape') this.closeMobileLightbox();
      else if (e.key === 'ArrowRight') this.lightboxGo(this.lightbox.index + 1);
      else if (e.key === 'ArrowLeft') this.lightboxGo(this.lightbox.index - 1);
    };
    document.addEventListener('keydown', this._lightboxKeyHandler);

    /* Recalcula limites de pan ao redimensionar */
    this._lightboxResizeHandler = () => {
      if (this.lightbox && this.lightbox.overlay.classList.contains('is-open')) {
        this.updateLightboxBounds();
      }
    };
    window.addEventListener('resize', this._lightboxResizeHandler);

    this.lightbox.image.addEventListener('load', () => this.updateLightboxBounds());

    this.setupLightboxGestures();
  }

  /* Pan 2D no mobile + swipe para trocar imagem quando não há overflow X */
  setupLightboxGestures() {
    const lb = this.lightbox;
    let startX = 0;
    let startY = 0;
    let startRatioX = 0.5;
    let startRatioY = 0.5;
    let axis = null;

    lb.stage.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      startRatioX = lb.panRatioX;
      startRatioY = lb.panRatio;
      axis = null;
      lb.image.classList.add('is-panning');
    }, { passive: true });

    lb.stage.addEventListener('touchmove', (e) => {
      if (e.touches.length !== 1) return;
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;
      if (!axis && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (axis === 'x' && lb.overflowX > 0) {
        e.preventDefault();
        this.applyLightboxPan(startRatioX - dx / lb.overflowX, startRatioY);
      } else if (axis === 'y' && lb.overflow > 0) {
        e.preventDefault();
        this.applyLightboxPan(startRatioX, startRatioY - dy / lb.overflow);
      }
    }, { passive: false });

    lb.stage.addEventListener('touchend', (e) => {
      lb.image.classList.remove('is-panning');
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      if (axis === 'x' && lb.overflowX === 0 && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
        this.lightboxGo(lb.index + (dx < 0 ? 1 : -1));
      }
    }, { passive: true });
  }

  applyLightboxPan(ratioX, ratioY) {
    const lb = this.lightbox;
    lb.panRatioX = Math.min(Math.max(ratioX, 0), 1);
    lb.panRatio = Math.min(Math.max(ratioY, 0), 1);
    lb.image.style.transform = `translate(${-lb.overflowX * lb.panRatioX}px, ${-lb.overflow * lb.panRatio}px)`;
  }

  updateLightboxBounds() {
    const lb = this.lightbox;
    if (!lb) return;
    const rect = lb.stage.getBoundingClientRect();
    if (this._isTouchDevice && lb.image.naturalWidth) {
      const scaleW = rect.width / lb.image.naturalWidth;
      const scaleH = rect.height / lb.image.naturalHeight;
      const scale = Math.max(scaleW, scaleH);
      lb.overflowX = Math.max(0, lb.image.naturalWidth * scale - rect.width);
      lb.overflow = Math.max(0, lb.image.naturalHeight * scale - rect.height);
    } else {
      lb.overflowX = 0;
      lb.overflow = Math.max(0, lb.image.offsetHeight - rect.height);
    }
    lb.stage.classList.toggle('is-pannable', lb.overflow > 0 || lb.overflowX > 0);
    this.applyLightboxPan(lb.panRatioX, lb.panRatio);
  }

  openMobileLightbox(index) {
    this.buildMobileLightbox();
    if (!this.lightbox) return;
    document.body.style.overflow = 'hidden';
    this.lightbox.overlay.classList.add('is-open');
    this.lightbox.overlay.setAttribute('aria-hidden', 'false');
    this.lightboxGo(index, true);
  }

  closeMobileLightbox() {
    if (!this.lightbox) return;
    this.lightbox.overlay.classList.remove('is-open');
    this.lightbox.overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (typeof this.goToMobileSlide === 'function') {
      this.goToMobileSlide(this.lightbox.index);
    }
  }

  lightboxGo(index, instant = false) {
    const lb = this.lightbox;
    const total = this.allMedia.length;
    if (index < 0) index = total - 1;
    if (index >= total) index = 0;
    lb.index = index;
    lb.panRatio = 0.5;
    lb.panRatioX = 0.5;

    const media = this.allMedia[index];
    const hiRes = media.src.replace(/width=\d+/, 'width=1600');
    if (instant) {
      lb.image.src = hiRes;
    } else {
      lb.image.classList.add('is-switching');
      const tmp = new Image();
      tmp.onload = () => {
        lb.image.src = hiRes;
        lb.image.classList.remove('is-switching');
      };
      tmp.src = hiRes;
    }
    lb.image.alt = media.alt || '';
    lb.counter.textContent = `${index + 1} / ${total}`;
    this.updateLightboxThumbs();
  }

  updateLightboxThumbs() {
    const lb = this.lightbox;
    if (!lb || !lb.thumbEls) return;
    lb.thumbEls.forEach((el, i) => el.classList.toggle('is-active', i === lb.index));
    const active = lb.thumbEls[lb.index];
    if (active) {
      active.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
  }

  /* ========== Bundle / Mix ========== */
  setupBundle() {
    const bundleBlock = this.container.querySelector('[data-bundle-block]');
    if (!bundleBlock) return;

    const totalPriceEl = this.container.querySelector('[data-bundle-total-price]');
    const addBtn = bundleBlock.querySelector('[data-bundle-add-btn]');
    const messageEl = bundleBlock.querySelector('[data-bundle-message]');

    let messageTimer = null;
    const showBundleMessage = (text, type = 'error') => {
      if (!messageEl) return;
      clearTimeout(messageTimer);
      messageEl.textContent = text;
      messageEl.classList.remove('bundle-message--success', 'bundle-message--error');
      messageEl.classList.add(type === 'success' ? 'bundle-message--success' : 'bundle-message--error');
      messageEl.hidden = false;
      messageTimer = setTimeout(() => {
        messageEl.hidden = true;
      }, 5000);
    };
    const clearBundleMessage = () => {
      if (!messageEl) return;
      clearTimeout(messageTimer);
      messageEl.hidden = true;
    };

    const showItemTooltip = (itemEl, text) => {
      const tip = itemEl.querySelector('[data-bundle-tooltip]');
      if (!tip) return;
      tip.textContent = text;
      tip.classList.add('visible');
      clearTimeout(tip._hideTimer);
      tip._hideTimer = setTimeout(() => tip.classList.remove('visible'), 3000);
    };

    const clearItemTooltips = () => {
      bundleBlock.querySelectorAll('[data-bundle-tooltip].visible').forEach(tip => {
        clearTimeout(tip._hideTimer);
        tip.classList.remove('visible');
      });
    };

    const getItemQuantity = (itemEl) => {
      const input = itemEl.querySelector('[data-bundle-qty-input]');
      if (!input) return 1;
      return Math.max(1, parseInt(input.value, 10) || 1);
    };

    const getItemMax = (itemEl) => {
      let max = Infinity;
      const ruleMax = parseInt(itemEl.dataset.ruleMax, 10);
      const invMax = parseInt(itemEl.dataset.inventoryMax, 10);
      if (!isNaN(ruleMax)) max = Math.min(max, ruleMax);
      if (!isNaN(invMax)) max = Math.min(max, invMax);
      return max;
    };

    const refreshStepper = (itemEl) => {
      const input = itemEl.querySelector('[data-bundle-qty-input]');
      if (!input) return;
      const plus = itemEl.querySelector('[data-bundle-qty-plus]');
      const minus = itemEl.querySelector('[data-bundle-qty-minus]');
      const max = getItemMax(itemEl);
      const q = Math.max(1, Math.min(getItemQuantity(itemEl), max));
      input.value = String(q).padStart(2, '0');
      if (plus) plus.disabled = q >= max;
      if (minus) minus.disabled = q <= 1;
    };

    const getVariants = (itemEl) => {
      const jsonScript = itemEl.querySelector('[data-bundle-variants-json]');
      if (!jsonScript) return null;
      try {
        return JSON.parse(jsonScript.textContent);
      } catch (e) {
        return null;
      }
    };

    const getSelectedOptions = (itemEl) => {
      const options = [];
      itemEl.querySelectorAll('[data-bundle-chip].selected').forEach(chip => {
        const idx = parseInt(chip.dataset.optionIndex, 10);
        options[idx] = chip.dataset.value;
      });
      return options;
    };

    const updateItemVariant = (itemEl) => {
      const variants = getVariants(itemEl);
      if (!variants) return;

      const selectedOptions = getSelectedOptions(itemEl);
      const match = variants.find(v =>
        selectedOptions.every((val, idx) => v[`option${idx + 1}`] === val)
      );
      if (!match) return;

      itemEl.dataset.variantId = match.id;
      itemEl.dataset.price = match.price;
      itemEl.dataset.compareAtPrice = match.compare_at_price || 0;

      const priceEl = itemEl.querySelector('[data-bundle-item-price]');
      if (priceEl) priceEl.textContent = this.formatMoney(match.price);

      const compareEl = itemEl.querySelector('[data-bundle-price-compare]');
      if (compareEl) {
        if (match.compare_at_price && match.compare_at_price > match.price) {
          compareEl.textContent = this.formatMoney(match.compare_at_price);
          compareEl.hidden = false;
        } else {
          compareEl.textContent = '';
          compareEl.hidden = true;
        }
      }

      // Refresh per-variant stock cap
      if (match.inventory_management && match.inventory_policy === 'deny') {
        itemEl.dataset.inventoryMax = match.inventory_quantity;
      } else {
        delete itemEl.dataset.inventoryMax;
      }
      refreshStepper(itemEl);
    };

    const updateTotal = () => {
      const items = bundleBlock.querySelectorAll('[data-bundle-item]');
      let total = 0;
      let compareTotal = 0;

      items.forEach(itemEl => {
        const checkbox = itemEl.querySelector('[data-bundle-checkbox]');
        if (!checkbox || !checkbox.checked) return;
        const qty = getItemQuantity(itemEl);
        const price = parseInt(itemEl.dataset.price, 10) || 0;
        const compareAt = parseInt(itemEl.dataset.compareAtPrice, 10) || 0;
        total += price * qty;
        compareTotal += (compareAt > price ? compareAt : price) * qty;
      });

      const priceWrapper = this.container.querySelector('[data-bundle-price-wrapper]')
        || this.container.querySelector('.product-price-block .price-wrapper');
      const visualPromotionActive = priceWrapper?.dataset.visualPromotion === 'true';
      const visualPromotionPercentage = Number(priceWrapper?.dataset.visualPromotionPercentage || 0);
      let displayedTotal = total;

      if (visualPromotionActive && visualPromotionPercentage > 0) {
        displayedTotal = Math.floor(total * (100 - visualPromotionPercentage) / 100);
      }

      if (totalPriceEl) {
        totalPriceEl.textContent = this.formatMoney(displayedTotal);
      }

      const priceEl = priceWrapper?.querySelector('[data-price]');
      if (priceEl) {
        priceEl.textContent = this.formatMoney(displayedTotal);
      }

      const priceRow = priceWrapper?.querySelector('[data-price-row]');
      const compareEl = priceWrapper?.querySelector('[data-price-compare]');
      const badgeEl = priceWrapper?.querySelector('[data-price-badge]');
      const showCompare = visualPromotionActive
        ? total > displayedTotal
        : compareTotal > total;

      if (priceRow && compareEl) {
        if (showCompare) {
          const compareValue = visualPromotionActive ? total : compareTotal;
          compareEl.textContent = this.formatMoney(compareValue);
          priceRow.hidden = false;
          if (badgeEl) {
            const discountPercent = visualPromotionActive
              ? visualPromotionPercentage
              : Math.floor(((compareTotal - total) * 100) / compareTotal);
            badgeEl.textContent = `${discountPercent}% OFF`;
            badgeEl.hidden = false;
          }
        } else {
          compareEl.textContent = '';
          priceRow.hidden = true;
          if (badgeEl) {
            badgeEl.textContent = '';
            badgeEl.hidden = true;
          }
        }
      }

      if (addBtn) {
        const isBundleSoldOut = bundleBlock.getAttribute('data-bundle-sold-out') === 'true';
        if (isBundleSoldOut) {
          addBtn.disabled = true;
          addBtn.textContent = addBtn.dataset.soldOutText || 'Esgotado';
        } else {
          const checkedCount = bundleBlock.querySelectorAll('[data-bundle-checkbox]:checked').length;
          addBtn.disabled = checkedCount === 0;
          if (checkedCount > 0) {
            addBtn.textContent = addBtn.dataset.addText || 'Adicionar combo ao carrinho';
          }
        }
      }
    };

    const items = bundleBlock.querySelectorAll('[data-bundle-item]');

    items.forEach(itemEl => {
      const checkbox = itemEl.querySelector('[data-bundle-checkbox]');

      if (checkbox) {
        checkbox.addEventListener('change', () => {
          itemEl.classList.toggle('bundle-item--disabled', !checkbox.checked);
          updateTotal();
        });

        // Tapping the image toggles the checkbox (image is outside the label).
        const imageEl = itemEl.querySelector('[data-bundle-item-image]');
        imageEl?.addEventListener('click', () => {
          if (checkbox.disabled) return;
          checkbox.checked = !checkbox.checked;
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      // Accordion expand / collapse
      itemEl.querySelectorAll('[data-bundle-accordion-toggle]').forEach(toggle => {
        toggle.addEventListener('click', () => {
          toggle.closest('[data-bundle-accordion]').classList.toggle('open');
        });
      });

      // Variant chips
      itemEl.querySelectorAll('[data-bundle-chip]').forEach(chip => {
        chip.addEventListener('click', () => {
          const idx = chip.dataset.optionIndex;
          const accordion = chip.closest('[data-bundle-accordion]');

          itemEl
            .querySelectorAll(`[data-bundle-chip][data-option-index="${idx}"]`)
            .forEach(c => c.classList.remove('selected'));
          chip.classList.add('selected');

          const valueEl = accordion.querySelector('[data-bundle-accordion-value]');
          if (valueEl) valueEl.textContent = chip.dataset.value;

          accordion.classList.remove('open');

          updateItemVariant(itemEl);
          updateTotal();
        });
      });

      // Quantity stepper
      const setQty = (wanted) => {
        const max = getItemMax(itemEl);
        if (wanted > max && max !== Infinity) {
          showItemTooltip(itemEl, max === 1 ? 'Máximo 1 unidade' : `Máximo ${max} unidades`);
        }
        const input = itemEl.querySelector('[data-bundle-qty-input]');
        if (input) input.value = String(Math.max(1, Math.min(wanted, max))).padStart(2, '0');
        refreshStepper(itemEl);
        updateTotal();
      };
      itemEl
        .querySelector('[data-bundle-qty-minus]')
        ?.addEventListener('click', () => setQty(getItemQuantity(itemEl) - 1));
      itemEl
        .querySelector('[data-bundle-qty-plus]')
        ?.addEventListener('click', () => setQty(getItemQuantity(itemEl) + 1));

      refreshStepper(itemEl);
    });

    if (addBtn) {
      addBtn.addEventListener('click', async (event) => {
        event.preventDefault();
        if (bundleBlock.getAttribute('data-bundle-sold-out') === 'true' || addBtn.disabled) return;

        const itemsToAdd = [];
        bundleBlock.querySelectorAll('[data-bundle-item]').forEach(itemEl => {
          const checkbox = itemEl.querySelector('[data-bundle-checkbox]');
          if (!checkbox || !checkbox.checked) return;
          const variantId = parseInt(itemEl.dataset.variantId, 10);
          if (!variantId) return;
          itemsToAdd.push({ itemEl, id: variantId, quantity: getItemQuantity(itemEl) });
        });

        if (itemsToAdd.length === 0) return;

        clearBundleMessage();
        clearItemTooltips();
        addBtn.classList.add('bundle-loading');

        const cartDrawer = document.querySelector('cart-drawer');
        const sections = cartDrawer ? cartDrawer.getSectionsToRender().map((s) => s.id) : [];

        try {
          // Single atomic request (efficient, avoids rate limits).
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: itemsToAdd.map(i => ({ id: i.id, quantity: i.quantity })),
              sections: sections.join(','),
              sections_url: window.location.pathname,
            }),
          });

          if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            const message = err.description || 'Não foi possível adicionar';

            // Pinpoint which checked item(s) are at their limit using cart state,
            // so the error shows as a tooltip on the exact product.
            let flagged = 0;
            try {
              const cart = await (await fetch('/cart.js')).json();
              itemsToAdd.forEach(item => {
                const max = getItemMax(item.itemEl);
                if (max === Infinity) return;
                const inCart = cart.items
                  .filter(ci => ci.variant_id === item.id)
                  .reduce((sum, ci) => sum + ci.quantity, 0);
                if (inCart + item.quantity > max) {
                  showItemTooltip(item.itemEl, message);
                  flagged++;
                }
              });
            } catch (e) {
              /* fall back to summary message below */
            }

            if (flagged === 0) {
              showBundleMessage(message, 'error');
            }
            return;
          }

          const data = await response.json();
          await this.updateCartCount();

          if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(data);
          } else {
            showBundleMessage('Combo adicionado ao carrinho!', 'success');
          }

          if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle', cartData: data });
          }
        } catch (error) {
          console.error('Bundle add to cart error:', error);
          showBundleMessage('Erro ao adicionar ao carrinho.', 'error');
        } finally {
          addBtn.classList.remove('bundle-loading');
        }
      });
    }

    updateTotal();
  }

  /* ========== Helper Methods ========== */
  getSectionId() {
    const section = this.container.closest('[id^="shopify-section-"]');
    return section?.id.replace('shopify-section-', '') || '';
  }

  formatMoney(cents) {
    const amount = cents / 100;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount);
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update cart count elements
      const countElements = document.querySelectorAll('[data-cart-count]');
      countElements.forEach(el => {
        el.textContent = cart.item_count;
      });
    } catch (error) {
      console.error('Erro ao atualizar contador do carrinho:', error);
    }
  }

  showMessage(text, type = 'success') {
    // Remove existing messages
    const existingMessage = document.querySelector('.product-message');
    if (existingMessage) {
      existingMessage.remove();
    }

    // Create new message
    const message = document.createElement('div');
    message.className = `product-message ${type}`;
    message.textContent = text;

    // Insert after add to cart button
    const addToCartBlock = document.querySelector('.product-add-to-cart');
    if (addToCartBlock) {
      addToCartBlock.appendChild(message);

      // Auto remove after 3 seconds
      setTimeout(() => {
        message.remove();
      }, 3000);
    }
  }
}

/* ========== Initialize ========== */
document.addEventListener('DOMContentLoaded', () => {
  new ProductCustom();
});

/* ========== Reinitialize on section reload (Theme Editor) ========== */
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new ProductCustom();
  });
}
